import React from 'react';
import {
    Box,
    Card,
    CardActionArea,
    CardContent,
    Collapse,
    Typography,
    useTheme,
    useMediaQuery,
    Tooltip,
    alpha,
} from '@mui/material';
import {
    AddCircleOutline,
    Description,
    Info,
    Speed,
    LightbulbOutlined,
    AddTask,
    Group,
    NoteAdd,
    Tune,
    CheckCircle,
    Star,
    Flag,
    RocketLaunch,
    Business,
    AccountTree,
    Edit,
    PlayArrow,
    TaskAlt,
    Monitor,
    Analytics,
    Input,
    Groups,
    TipsAndUpdates,
    AutoFixHigh,
} from '@mui/icons-material';

interface PromptSuggestion {
    id: string;
    title: string;
    description: string;
    prompt: string;
    icon: string;
    category: string;
}

interface Category {
    id: string;
    title: string;
    description: string;
    icon: string;
    promptIds: string[];
}

interface PromptSuggestProps {
    onPromptSelect: (prompt: string) => void;
    categories?: Category[];
    suggestions?: PromptSuggestion[];
}
const iconMap: { [key: string]: React.ElementType } = {
    add_circle: AddCircleOutline,
    description: Description,
    info: Info,
    speed: Speed,
    lightbulb: LightbulbOutlined,
    add_task: AddTask,
    group: Group,
    note_add: NoteAdd,
    tune: Tune,
    check_circle: CheckCircle,
    star: Star,
    flag: Flag,
    rocket_launch: RocketLaunch,
    business: Business,
    account_tree: AccountTree,
    edit: Edit,
    play_arrow: PlayArrow,
    task_alt: TaskAlt,
    monitoring: Monitor,
    analytics: Analytics,
    input: Input,
    groups: Groups,
    tips_and_updates: TipsAndUpdates,
    magic: AutoFixHigh,
};

/**
 * Detects whether the rendered text of a ref'd HTML element overflows
 * beyond the given line count. Returns `true` once the content has more
 * lines than `maxLines` would allow (after the element is laid out at
 * its real width). We use this to enable `-webkit-line-clamp` only when
 * the text is actually too long, so short text stays compact and long
 * text gracefully expands.
 *
 * Re-measures on every render via `useLayoutEffect` so a translation or
 * font swap is picked up immediately. The element must be rendered with
 * `display: block` (or the default) for `scrollHeight` to reflect line
 * count correctly.
 */
function useNeedsClamp(
    ref: React.RefObject<HTMLElement>,
    text: string,
    maxLines: number,
    lineHeight: number,
): boolean {
    const [needsClamp, setNeedsClamp] = React.useState(false);

    React.useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        // If the rendered height exceeds what `maxLines` would allow,
        // the user is missing content and we should clamp + ellipsise.
        const allowed = maxLines * lineHeight;
        setNeedsClamp(el.scrollHeight > allowed + 1); // +1 to absorb sub-pixel rounding
    }, [text, maxLines, lineHeight, ref]);

    return needsClamp;
}

interface CategoryCardProps {
    category: Category;
    isExpanded: boolean;
    onToggle: () => void;
}

/**
 * Single category tile shown in the horizontal scroller.
 *
 * Sizing strategy:
 * - Desktop keeps the original fixed 110px height; tooltips surface the
 *   full title and description on hover so truncation is invisible.
 * - Mobile starts compact (auto height, ~110px when text fits in 2 lines
 *   for the title and 2 lines for the description) and grows only when
 *   `useNeedsClamp` detects that the actual rendered text would
 *   otherwise be clipped. This keeps short cards compact while still
 *   giving long-text cards the room they need.
 */
const CategoryCard: React.FC<CategoryCardProps> = ({
    category,
    isExpanded,
    onToggle,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const titleRef = React.useRef<HTMLDivElement>(null);
    const descRef = React.useRef<HTMLDivElement>(null);
    const IconComp = iconMap[category.icon] || AutoFixHigh;

    // Title: 2 lines on desktop, 2 on mobile *unless* the text really
    // needs more — in that case we let it grow up to 3 lines (mobile)
    // before clamping. Line-height comes from the Typography sx below
    // (1.25) and font size is 0.9rem on desktop / 0.85rem on mobile.
    const titleFontSize = isMobile ? 0.85 : 0.9; // rem
    const titleLineHeightPx = titleFontSize * 1.25 * 16; // rem -> px (16px root)
    const titleNeeds3Lines = useNeedsClamp(titleRef, category.title, 2, titleLineHeightPx);
    const titleClamp = titleNeeds3Lines ? 3 : 2;

    // Description: 2 lines on desktop, 2 on mobile *unless* the text
    // really needs more — then up to 4 lines on mobile before clamping.
    const descFontSize = isMobile ? 0.75 : 0.78;
    const descLineHeightPx = descFontSize * 1.4 * 16;
    const descNeedsMore = useNeedsClamp(descRef, category.description, 2, descLineHeightPx);
    // On mobile: 2 -> 3 -> 4 lines as needed. On desktop: always 2.
    const descClamp = !isMobile ? 2 : descNeedsMore ? 4 : 3;

    return (
        <Card
            variant="outlined"
            sx={{
                minWidth: { xs: 200, sm: 240 },
                maxWidth: { xs: 200, sm: 240 },
                // Auto height on mobile so the card grows only when text
                // needs more space; desktop keeps the original fixed card.
                height: { xs: 'auto', sm: 110 },
                flexShrink: 0,
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: isExpanded ? 'primary.main' : 'divider',
                borderRadius: 5,
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    borderColor: 'primary.light',
                    boxShadow: '0 4px 12px rgba(84,189,193,0.10)',
                }
            }}
        >
            <CardActionArea
                onClick={onToggle}
                sx={{ height: '100%', borderRadius: 5 }}
            >
                <CardContent
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        p: 2,
                        // Override the description's display once we know
                        // we are NOT clamping: the inner Typography below
                        // applies the -webkit-line-clamp sx unconditionally
                        // (the visual cutoff is fine either way), but we
                        // bump the row gap so the "expanded" look feels
                        // airy.
                        gap: 0.75,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.25,
                            mb: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                                borderRadius: 3,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                                color: 'primary.dark',
                                '& svg': {
                                    fontSize: 18
                                }
                            }}
                        >
                            <IconComp />
                        </Box>
                        <Tooltip
                            title={category.title}
                            placement="bottom-start"
                            enterDelay={500}
                            leaveDelay={200}
                            disableInteractive={false}
                        >
                            <Typography
                                ref={titleRef}
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    fontSize: { xs: '0.85rem', sm: '0.9rem' },
                                    // Clamp only kicks in once the text
                                    // really overflows. Short titles stay
                                    // on a single line; long ones expand
                                    // up to 3 lines on mobile / 2 on
                                    // desktop.
                                    display: '-webkit-box',
                                    WebkitLineClamp: titleClamp,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: titleNeeds3Lines || !isMobile ? 'hidden' : 'visible',
                                    lineHeight: 1.25,
                                    cursor: 'pointer',
                                    minWidth: 0,
                                    flex: 1,
                                }}
                            >
                                {category.title}
                            </Typography>
                        </Tooltip>
                    </Box>

                    <Tooltip
                        title={category.description}
                        placement="bottom-start"
                        enterDelay={500}
                        leaveDelay={200}
                        disableInteractive={false}
                        sx={{
                            '& .MuiTooltip-tooltip': {
                                maxWidth: 300,
                                fontSize: '0.875rem',
                            }
                        }}
                    >
                        <Typography
                            ref={descRef}
                            variant="body2"
                            sx={{
                                color: 'text.secondary',
                                lineHeight: 1.4,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                // 2 lines on desktop; 3 on mobile, 4 when
                                // the description really needs more.
                                WebkitLineClamp: descClamp,
                                WebkitBoxOrient: 'vertical',
                                fontSize: { xs: '0.75rem', sm: '0.78rem' },
                                cursor: 'pointer'
                            }}
                        >
                            {category.description}
                        </Typography>
                    </Tooltip>
                </CardContent>
            </CardActionArea>
        </Card>
    );
};

const PromptSuggest: React.FC<PromptSuggestProps> = ({
    onPromptSelect,
    categories,
    suggestions,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expandedId, setExpandedId] = React.useState<string | null>(null);

    // Build a quick lookup of suggestions by id
    const promptById = React.useMemo(() => {
        const map = new Map<string, PromptSuggestion>();
        (suggestions || []).forEach((s) => map.set(s.id, s));
        return map;
    }, [suggestions]);

    const handleCardClick = (prompt: string) => {
        onPromptSelect(prompt);
    };

    const handleCategoryToggle = (categoryId: string) => {
        setExpandedId((prev) => (prev === categoryId ? null : categoryId));
    };

    if (!categories || categories.length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                py: 2,
                px: { xs: 1, sm: 2 }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'visible',
                        gap: 2,
                        pb: 1,
                        pt: 1,
                        px: 1,
                    scrollBehavior: 'smooth',
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 4,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.action.disabled,
                        borderRadius: 4,
                        '&:hover': {
                            backgroundColor: theme.palette.action.selected,
                        }
                    },
                    ...(isMobile && {
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {
                            display: 'none',
                        }
                    })
                }}
            >
                {categories.map((category) => {
                    const isExpanded = expandedId === category.id;
                    return (
                        <CategoryCard
                            key={category.id}
                            category={category}
                            isExpanded={isExpanded}
                            onToggle={() => handleCategoryToggle(category.id)}
                        />
                    );
                })}
            </Box>

            {/* Expanded prompts for the selected category */}
            {categories.map((category) => {
                const isExpanded = expandedId === category.id;
                if (!isExpanded) return null;
                const prompts = category.promptIds
                    .map((pid) => promptById.get(pid))
                    .filter((p): p is PromptSuggestion => Boolean(p));
                if (prompts.length === 0) return null;

                return (
                    <Collapse key={`expanded-${category.id}`} in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                            sx={{
                                mt: 1.5,
                                mx: 1,
                                p: 1.5,
                                borderRadius: 4,
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
                                border: '1px solid',
                                borderColor: (t) => alpha(t.palette.primary.main, 0.18),
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                            }}
                        >
                            {prompts.map((prompt) => {
                                const IconComponent = iconMap[prompt.icon] || AutoFixHigh;
                                return (
                                    <Box
                                        key={prompt.id}
                                        onClick={() => handleCardClick(prompt.prompt)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                            p: { xs: 1.5, sm: 1.25 },
                                            borderRadius: 2,
                                            cursor: 'pointer',
                                            bgcolor: 'background.paper',
                                            border: '1px solid',
                                            borderColor: 'transparent',
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.10),
                                                borderColor: (t) => alpha(t.palette.primary.main, 0.25),
                                                transform: 'translateX(2px)',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 28,
                                                height: 28,
                                                borderRadius: 1.5,
                                                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                                                color: 'primary.main',
                                                flexShrink: 0,
                                                '& svg': { fontSize: 15 },
                                            }}
                                        >
                                            <IconComponent />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Tooltip
                                                title={prompt.title}
                                                placement="left-start"
                                                enterDelay={500}
                                                leaveDelay={200}
                                                disableInteractive={false}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={500}
                                                    sx={{
                                                        color: 'text.primary',
                                                        fontSize: '0.82rem',
                                                        lineHeight: 1.3,
                                                        // Clamp on mobile so a long
                                                        // prompt title stays inside
                                                        // its row instead of forcing
                                                        // the row to grow taller
                                                        // than the rest of the list.
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {prompt.title}
                                                </Typography>
                                            </Tooltip>
                                            <Tooltip
                                                title={prompt.description}
                                                placement="left-start"
                                                enterDelay={500}
                                                leaveDelay={200}
                                                disableInteractive={false}
                                                sx={{
                                                    '& .MuiTooltip-tooltip': {
                                                        maxWidth: 300,
                                                        fontSize: '0.8rem',
                                                    }
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary',
                                                        fontSize: '0.72rem',
                                                        lineHeight: 1.3,
                                                        display: '-webkit-box',
                                                        // Bumped from 1 to 3 on
                                                        // mobile: hover-tooltips
                                                        // don't work on touch, so
                                                        // we surface two extra
                                                        // lines inline.
                                                        WebkitLineClamp: { xs: 3, sm: 1 },
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    {prompt.description}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Collapse>
                );
            })}
        </Box>
    );
};

export default PromptSuggest;